import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useParams, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import {
  FileText, MessageSquare,
  LayoutDashboard, Image, Calendar, Activity, AlertTriangle, Search,
  Shield, Link2, User, Scale,
} from 'lucide-react';
import { caseApi, documentApi } from '../services/api';
import { httpClient } from '../services/httpClient';
import { DocumentsTab } from '../components/tabs/DocumentsTab';
import { ClinicalStandardsTab } from '../components/review/ClinicalStandardsTab';
import { EvidenceIntegrityTab } from '../components/review/EvidenceIntegrityTab';
import { CausationAnalysisTab } from '../components/review/CausationAnalysisTab';
import { CaseOverviewSubtab } from '../components/review/CaseOverviewSubtab';
import { ChronologyTab } from '../components/tabs/ChronologyTab';
import { ExpertEvidenceTab } from '../components/tabs/ExpertEvidenceTab';
import { ImagesTab } from '../components/tabs/ImagesTab';
import { BodyMapSubtab } from '../components/review/BodyMapSubtab';
import { ClaimantBackgroundTab } from '../components/review/ClaimantBackgroundTab';
import { WarningBanner } from '../components/review/WarningBanner';
import { QueuedCaseBanner } from '../components/QueuedCaseBanner';
import { KeyFindingsProvider } from '../contexts/KeyFindingsContext';
import { TabConfig } from '../types';
import { formatDateTime, formatSmartDateTime } from '../utils/dateFormat';
import { useRegisterAskContext } from '../hooks/useRegisterAskContext';
import { useMetadataExtraction } from '../hooks/useMetadataExtraction';
import { useAskSidebar } from '../contexts/AskSidebarContext';
import { useDocumentViewer } from '../contexts/DocumentViewerContext';
import { AgentFullView } from '../components/agent/AgentFullView';
import { useStructuredPiiRegistration } from '../hooks/useStructuredPiiRegistration';
import { useRedactionMode } from '../contexts/RedactionModeContext';
import { useOpenCases } from '../contexts/OpenCasesContext';
import { KeyFindingsIndicator } from '../components/KeyFindingsIndicator';
import { ProcessingProgress } from '../components/ProcessingProgress';
import { AnalysisProgressPanel } from '../components/AnalysisProgressPanel';
import { DownloadsButton } from '../components/DownloadsButton';

export const CaseDetailPage: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Case data
  const { data: caseData, isLoading, error } = useQuery(
    ['case', caseId],
    () => caseApi.getCase(caseId!),
    {
      enabled: !!caseId,
      refetchInterval: (data) => {
        const s = data?.status;
        return (s === 'PROCESSING' || s === 'QUEUED') ? 3000 : false;
      }
    }
  );

  // Open-cases tab bar — read persisted status early so the extraction hook
  // can seed its state and avoid flashing "Processed" while checking progress.
  const { openCases, openCase, updateCaseTab } = useOpenCases();
  const persistedTabStatus = openCases.find(t => t.caseId === caseId)?.status;

  // Metadata extraction orchestration (replaces ReviewTab logic)
  const extraction = useMetadataExtraction(caseId!, caseData || undefined, persistedTabStatus);
  const { hasMetadata, hasDocuments, documents, metadata, analysisInProgress, loadingMetadata,
          handleExtractMetadata, handleAnalysisComplete, warnings, isPartial,
          creditShortfall, dismissCreditShortfall, queued, queuedPageLimit } = extraction;

  // Register structured PII for redaction mode (patient names, DOBs, identifiers)
  useStructuredPiiRegistration(caseData || undefined, metadata?.patients);

  // Check if case name contains registered PII (blur title only when it does)
  const { isEnabled: redactionEnabled, structuredPii } = useRedactionMode();
  const caseNameHasPii = redactionEnabled && !!caseData?.name && [...structuredPii].some(pii =>
    caseData.name.toLowerCase().includes(pii.toLowerCase())
  );

  // Pipeline still running? Sidebar shouldn't render until analysis finishes.
  const pipelineRunning = caseData?.status?.toUpperCase() === 'PROCESSING' || analysisInProgress;
  const sidebarReady = hasMetadata && !pipelineRunning;

  // Register context with the unified Ask sidebar — only when analysis is done
  useRegisterAskContext({ pageType: sidebarReady ? 'case-detail' : 'other', caseId: caseId || undefined });

  // Detect when sidebar is open (for responsive tab compaction)
  const { isOpen: isSidebarOpen, close: closeSidebar, isMaximized, toggleMaximize, openReport } = useAskSidebar();

  // Open a specific report in the wizard sidebar when navigated from Reports/Dashboard.
  // Capture the ID in a ref immediately — internal <Navigate replace /> redirects
  // (e.g. overview → records while metadata loads) strip location.state.
  //
  // NOTE: we deliberately do NOT clear the ref when firing openReport. In
  // React.StrictMode, effects are double-invoked: the first run calls
  // openReport(R), then the simulated unmount fires useRegisterAskContext's
  // cleanup which resets pageContext to 'other' — that transition clears
  // pendingReportId/isOpen in AskSidebarContext. The simulated remount then
  // needs to re-call openReport(R) from the same ref. openReport is idempotent
  // (it just writes a few state atoms), so calling it again on remount
  // restores the state wiped by the cleanup.
  const pendingOpenReportRef = React.useRef<string | null>(
    (location.state as { openReportId?: string } | null)?.openReportId ?? null
  );
  // Also pick up any new navigation state (e.g. if the user clicks another report link)
  const incomingOpenReportId = (location.state as { openReportId?: string } | null)?.openReportId;
  if (incomingOpenReportId && incomingOpenReportId !== pendingOpenReportRef.current) {
    pendingOpenReportRef.current = incomingOpenReportId;
  }
  useEffect(() => {
    const reportId = pendingOpenReportRef.current;
    if (!reportId || !sidebarReady) return;
    openReport(reportId);
  }, [sidebarReady]); // eslint-disable-line react-hooks/exhaustive-deps
  const { activeDocument } = useDocumentViewer();

  // Case Brief collapsed state — persisted to sessionStorage so it survives
  // tab switches AND any unexpected component remounts.
  const caseBriefKey = `caseBrief-open-${caseId}`;
  const [caseBriefOpen, setCaseBriefOpen] = useState<boolean | null>(() => {
    try {
      const stored = sessionStorage.getItem(caseBriefKey);
      return stored !== null ? stored === 'true' : null;
    } catch { return null; }
  });
  useEffect(() => {
    if (caseBriefOpen === null && caseData) {
      setCaseBriefOpen(false);
    }
  }, [caseData, caseBriefOpen]);
  useEffect(() => {
    if (caseBriefOpen !== null) {
      try { sessionStorage.setItem(caseBriefKey, String(caseBriefOpen)); } catch {}
    }
  }, [caseBriefOpen, caseBriefKey]);

  // DICOM studies query — unlocks Images/Overview even without text metadata
  const { data: dicomStudies } = useQuery(
    ['dicom-studies', caseId],
    () => documentApi.getDicomStudies(caseId!),
    {
      enabled: !!caseId,
      staleTime: 30000,
      retry: 3,
      retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 10000),
      onError: (err: unknown) => console.error('Failed to fetch DICOM studies for case', caseId, err),
    }
  );
  const hasDicomStudies = (dicomStudies?.length || 0) > 0;

  const actualDocumentCount = documents.length || caseData?.documentCount || 0;

  // Compute display status: backend doesn't track "PROCESSED"/"ANALYSING" — derive from metadata/progress
  const displayStatus = (() => {
    const raw = caseData?.status?.toUpperCase() || 'CREATED';
    if (raw === 'PROCESSING') return 'PROCESSING';
    if (raw === 'ERROR') return 'ERROR';
    // Check analysisInProgress BEFORE hasMetadata — metadata arrives partway
    // through the pipeline while later stages are still running.
    if (analysisInProgress) return 'ANALYSING';
    if (hasMetadata && isPartial) return 'PARTIAL';
    if (hasMetadata) return 'PROCESSED';
    // Show QUEUED only when there's no metadata yet (i.e. not yet processed)
    if (raw === 'QUEUED' || queued) return 'QUEUED';
    return raw;
  })();

  // Open-cases tab bar — auto-register and keep in sync
  useEffect(() => {
    if (caseId) openCase(caseId, caseData?.name || 'Loading...');
  }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (caseId && caseData) updateCaseTab(caseId, { name: caseData.name, status: displayStatus });
  }, [caseId, caseData?.name, displayStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remember the last-visited subtab per case so switching via the tab bar
  // returns you to where you left off (rather than always falling through to
  // defaultTab, which collapses everyone to records while metadata loads).
  useEffect(() => {
    if (!caseId) return;
    const pathAfterCase = location.pathname.split(`/cases/${caseId}/`)[1] || '';
    const segment = pathAfterCase.split('/')[0];
    if (segment) updateCaseTab(caseId, { lastTab: segment });
  }, [caseId, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll progress for all open cases that are analysing (including active — so
  // progress is available immediately when user switches away to another tab)
  const analysingCases = openCases.filter(
    t => t.status === 'ANALYSING' || t.status === 'PROCESSING'
  );
  useEffect(() => {
    if (analysingCases.length === 0) return;
    const poll = async () => {
      for (const tab of analysingCases) {
        try {
          const data = await httpClient.get<{ progress: number; complete: boolean; failed: boolean }>(
            `/intelligence/metadata/progress/${tab.caseId}`
          );
          const pct = Math.round((data.progress ?? 0) * 100);
          if (pct !== tab.progress) {
            updateCaseTab(tab.caseId, { progress: pct });
          }
          if (data.complete || data.failed) {
            updateCaseTab(tab.caseId, { status: data.complete ? 'COMPLETE' : 'FAILED', progress: data.complete ? 100 : tab.progress });
          }
        } catch { /* ignore */ }
      }
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [analysingCases.map(t => t.caseId).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tabs stay locked until the entire pipeline finishes — metadata arrives
  // partway through (after "Review"), but later stages are still running.
  const tabsReady = sidebarReady;

  // Flat tab structure
  const tabs: TabConfig[] = [
    { id: 'records',              label: 'Records',                icon: 'FileText',       disabled: false },
    { id: 'overview',             label: 'Overview',               icon: 'LayoutDashboard', disabled: !tabsReady },
    { id: 'claimant-background',  label: 'Claimant',               icon: 'User',            disabled: !tabsReady },
    { id: 'chronology',           label: 'Chronology',             icon: 'Calendar',        disabled: !tabsReady },
    { id: 'expert-evidence',      label: 'Expert Evidence',        icon: 'Scale',           disabled: !tabsReady },
    { id: 'images',               label: 'Images',                 icon: 'Image',           disabled: !tabsReady },
    { id: 'body-map',             label: 'Body Map',               icon: 'Activity',        disabled: !tabsReady },
    { id: 'clinical-standards',  label: 'Clinical Standards',     icon: 'Shield',           disabled: !tabsReady },
    { id: 'evidence-integrity',  label: 'Evidence Integrity',     icon: 'Search',           disabled: !tabsReady },
    { id: 'causation',           label: 'Causation',              icon: 'Link2',            disabled: !tabsReady },
  ];

  // Default tab depends on whether the case is fully processed.
  // We only consult this AFTER metadata has loaded (see the Route gates below) —
  // otherwise tabsReady is momentarily false during the load and would push the
  // user to records even on processed cases.
  const defaultTab = tabsReady ? 'overview' : 'records';

  const getCurrentTab = () => {
    // Extract the segment right after /cases/:caseId/ to handle sub-paths like /drafts/generate
    const pathAfterCase = location.pathname.split(`/cases/${caseId}/`)[1] || '';
    const tabSegment = pathAfterCase.split('/')[0];
    return tabs.find(tab => tab.id === tabSegment)?.id || defaultTab;
  };

  const getTabIcon = (iconName: string) => {
    const icons: Record<string, React.FC<{ className?: string }>> = {
      LayoutDashboard,
      FileText,
      Image,
      Calendar,
      Activity,
      AlertTriangle,
      Search,
      Shield,
      Link2,
      MessageSquare,
      User,
      Scale,
    };
    const Icon = icons[iconName] || FileText;
    return <Icon className="h-4 w-4" />;
  };

  // Responsive tab bar — only go icon-only when both sidebar and doc panel are open
  const tabWrapperRef = useRef<HTMLDivElement>(null);
  const [compactTabs, setCompactTabs] = useState(false);

  useEffect(() => {
    const wrapper = tabWrapperRef.current;
    if (!wrapper) return;

    const observer = new ResizeObserver(() => {
      const nav = wrapper.querySelector('nav');
      if (!nav) return;
      // Compact when the nav's full content overflows the available wrapper width
      setCompactTabs(nav.scrollWidth > wrapper.clientWidth);
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, []);

  // Override: only compact when sidebar is open
  const effectiveCompact = compactTabs && isSidebarOpen;

  const renderTab = (tab: TabConfig, isActive: boolean) => {
    if (tab.disabled) {
      return (
        <span
          key={tab.id}
          className={`relative group/tab flex-shrink-0 flex items-center ${effectiveCompact ? 'p-1.5' : 'space-x-1.5 py-1.5 px-2.5'} font-medium text-sm text-gray-400 dark:text-medical-600 cursor-not-allowed opacity-50`}
          title={tab.label}
        >
          {getTabIcon(tab.icon!)}
          {!effectiveCompact && <span className="whitespace-nowrap">{tab.label}</span>}
          {effectiveCompact && (
            <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover/tab:opacity-100 pointer-events-none transition-opacity z-50">
              {tab.label}
            </span>
          )}
        </span>
      );
    }

    return (
      <Link
        key={tab.id}
        to={`/cases/${caseId}/${tab.id}`}
        onClick={() => { if (isSidebarOpen) closeSidebar(); if (isMaximized) toggleMaximize(); }}
        className={`
          relative group/tab flex-shrink-0 flex items-center ${effectiveCompact ? 'p-1.5' : 'space-x-1.5 py-1.5 px-2.5'} font-medium text-sm transition-all rounded-md
          ${isActive && !isMaximized
            ? 'text-cyan-600 bg-cyan-50 dark:text-cyan-400 dark:bg-cyan-900/30'
            : 'text-gray-700 dark:text-medical-300 hover:text-gray-900 dark:hover:text-medical-100 hover:bg-gray-50 dark:hover:bg-medical-700'
          }
        `}
        title={effectiveCompact ? tab.label : undefined}
      >
        {getTabIcon(tab.icon!)}
        {!effectiveCompact && <span className="whitespace-nowrap">{tab.label}</span>}
        {effectiveCompact && (
          <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover/tab:opacity-100 pointer-events-none transition-opacity z-50">
            {tab.label}
          </span>
        )}
      </Link>
    );
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-medical-600 dark:text-medical-400">Loading case...</span>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="text-center">
            <FileText className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-2">Case Not Found</h3>
            <p className="text-medical-600 dark:text-medical-400 mb-4">
              The requested case could not be found or you don't have access to it.
            </p>
            <Link to="/cases" className="btn-primary">
              Back to Cases
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <KeyFindingsProvider caseId={caseId!}>
      <div className="flex flex-col h-full">
        {/* Status indicators row — hidden during analysis since the progress panel replaces it */}
        {!analysisInProgress && (
          <div className="bg-medical-50 dark:bg-medical-900 px-4 pb-0" style={{ paddingRight: `calc(var(--sidebar-width, 0px) + 1rem)` }}>
            <div className="flex items-center justify-end gap-2 py-1.5">
              {hasMetadata && <KeyFindingsIndicator />}
              <DownloadsButton caseId={caseId!} hasMetadata={!!hasMetadata} />
              {displayStatus === 'PROCESSING' && (
                <ProcessingProgress stages={caseData.processingProgress} />
              )}
              <div className="relative group">
                <div
                  className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap cursor-default ${
                    displayStatus === 'PROCESSED' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                    displayStatus === 'PARTIAL' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                    displayStatus === 'READY' ? 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300' :
                    displayStatus === 'ANALYSING' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' :
                    displayStatus === 'PROCESSING' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                    displayStatus === 'QUEUED' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                    displayStatus === 'ERROR' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  {displayStatus.charAt(0) + displayStatus.slice(1).toLowerCase()}
                </div>
                {displayStatus === 'PROCESSED' && metadata && (
                  <div className="absolute right-0 top-full mt-1.5 z-50 hidden group-hover:block">
                    <div className="bg-white dark:bg-medical-800 rounded-lg shadow-lg border border-gray-200 dark:border-medical-600 px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-medical-200">
                        <span className="font-medium">{documents.length} doc{documents.length !== 1 ? 's' : ''}</span>
                        <span className="text-gray-300 dark:text-medical-500">&middot;</span>
                        <span className="font-medium">{documents.reduce((sum, d: any) => sum + (d.pageCount || 0), 0)} pages</span>
                        {metadata.durationSeconds > 0 && (<>
                          <span className="text-gray-300 dark:text-medical-500">&middot;</span>
                          <span className="font-medium">{metadata.durationSeconds < 60 ? `${metadata.durationSeconds}s` : `${Math.floor(metadata.durationSeconds / 60)}m${metadata.durationSeconds % 60 > 0 ? ` ${metadata.durationSeconds % 60}s` : ''}`}</span>
                        </>)}
                      </div>
                      {metadata.completedAt && (
                        <div className="text-[11px] text-gray-400 dark:text-medical-400 mt-0.5">
                          {new Date(metadata.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} at {new Date(metadata.completedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Flat Tab Navigation */}
        <div ref={tabWrapperRef} className="bg-white dark:bg-medical-800 border-b border-medical-200 dark:border-medical-700 px-6 py-2 overflow-x-auto scrollbar-hidden" style={{ paddingRight: `calc(var(--sidebar-width, 0px) + 1.5rem)` }}>
          <nav className="flex items-center gap-0.5 flex-nowrap min-w-0">
            {tabs.map(tab => renderTab(tab, getCurrentTab() === tab.id))}
          </nav>
        </div>

        {/* Queued Case Banner — shown when case exceeds page limit */}
        {displayStatus === 'QUEUED' && (
          <div className="px-6 py-3 bg-white dark:bg-medical-800 border-b border-medical-200 dark:border-medical-700" style={{ paddingRight: `calc(var(--sidebar-width, 0px) + 1.5rem)` }}>
            <QueuedCaseBanner pageLimit={queuedPageLimit} />
          </div>
        )}

        {/* Analysis Progress — shown below tabs when running */}
        {analysisInProgress && (
          <div className="px-4 pt-2 pb-1 bg-white dark:bg-medical-800 border-b border-medical-200 dark:border-medical-700" style={{ paddingRight: `calc(var(--sidebar-width, 0px) + 1rem)` }}>
            <AnalysisProgressPanel
              documents={documents}
              caseId={caseId!}
              onRetry={() => handleExtractMetadata()}
              onComplete={async (success) => {
                handleAnalysisComplete();
                if (success) {
                  await queryClient.invalidateQueries(['metadata', caseId]);
                  navigate(`/cases/${caseId}/overview`);
                }
              }}
            />
          </div>
        )}


        {/* Warning Banner for partial analysis */}
        {displayStatus === 'PARTIAL' && warnings.length > 0 && (
          <div className="px-6 py-3 bg-white dark:bg-medical-800 border-b border-medical-200 dark:border-medical-700" style={{ paddingRight: `calc(var(--sidebar-width, 0px) + 1.5rem)` }}>
            <WarningBanner warnings={warnings} />
          </div>
        )}

        {/* Tab Content */}
        <div className="flex-1 min-h-0 overflow-hidden" style={{ paddingRight: 'var(--sidebar-width, 0px)' }}>
          {isMaximized ? (
            <AgentFullView caseId={caseId!} />
          ) : (
          <Routes>
            <Route path="/" element={loadingMetadata ? null : <Navigate to={defaultTab} replace />} />
            <Route path="/records" element={
              <DocumentsTab
                caseId={caseId!}
                caseData={caseData}
                onStartAnalysis={handleExtractMetadata}
                hasMetadata={hasMetadata}
                analysisInProgress={analysisInProgress}
                caseBriefOpen={caseBriefOpen}
                onCaseBriefToggle={setCaseBriefOpen}
                creditShortfall={creditShortfall}
                onDismissCreditShortfall={dismissCreditShortfall}
              />
            } />
            {/*
              Tabs that gate on `tabsReady` must NOT redirect while metadata is
              still loading. If they did, a direct entry to e.g. /overview from
              the reports page would bounce to /records before metadata had a
              chance to arrive — and the redirect strips location.state.
              We render null until loadingMetadata settles, then either show
              the tab (if ready) or redirect (if the case truly has no data).
            */}
            <Route path="/overview" element={
              loadingMetadata ? null :
              tabsReady ? <CaseOverviewSubtab caseId={caseId!} /> : <Navigate to="../records" replace />
            } />
            <Route path="/claimant-background" element={
              loadingMetadata ? null :
              tabsReady ? <ClaimantBackgroundTab caseId={caseId!} /> : <Navigate to="../overview" replace />
            } />
            <Route path="/chronology" element={
              loadingMetadata ? null :
              tabsReady ? (
                <div className="h-full min-h-0">
                  <ChronologyTab caseId={caseId!} />
                </div>
              ) : <Navigate to="../overview" replace />
            } />
            <Route path="/expert-evidence" element={
              loadingMetadata ? null :
              tabsReady ? <ExpertEvidenceTab caseId={caseId!} /> : <Navigate to="../overview" replace />
            } />
            <Route path="/images" element={
              loadingMetadata ? null :
              tabsReady ? <ImagesTab caseId={caseId!} /> : <Navigate to="../records" replace />
            } />
            <Route path="/body-map" element={
              loadingMetadata ? null :
              tabsReady ? <BodyMapSubtab caseId={caseId!} /> : <Navigate to="../overview" replace />
            } />
            <Route path="/clinical-standards" element={
              loadingMetadata ? null :
              tabsReady ? <ClinicalStandardsTab caseId={caseId!} /> : <Navigate to="../overview" replace />
            } />
            <Route path="/evidence-integrity" element={
              loadingMetadata ? null :
              tabsReady ? <EvidenceIntegrityTab caseId={caseId!} /> : <Navigate to="../overview" replace />
            } />
            <Route path="/causation" element={
              loadingMetadata ? null :
              tabsReady ? <CausationAnalysisTab caseId={caseId!} /> : <Navigate to="../overview" replace />
            } />
            <Route path="/drafts/*" element={<Navigate to="../overview" replace />} />
            <Route path="/drafts" element={<Navigate to="../overview" replace />} />
            <Route path="/reports/*" element={<Navigate to="../overview" replace />} />
            <Route path="/reports" element={<Navigate to="../overview" replace />} />
            {/* Legacy routes */}
            <Route path="/review" element={<Navigate to="../overview" replace />} />
            <Route path="/documents" element={<Navigate to="../records" replace />} />
            <Route path="/evidence" element={<Navigate to="../records" replace />} />
            <Route path="/exports" element={<Navigate to="../overview" replace />} />
            <Route path="/patients" element={<Navigate to="../overview" replace />} />
            <Route path="/focused-analysis" element={<Navigate to="../clinical-standards" replace />} />
            <Route path="/objectives" element={<Navigate to="../clinical-standards" replace />} />
            <Route path="/standard-of-care" element={<Navigate to="../clinical-standards" replace />} />
            <Route path="/comparative" element={<Navigate to="../clinical-standards" replace />} />
            <Route path="/gaps" element={<Navigate to="../evidence-integrity" replace />} />
            <Route path="/inconsistencies" element={<Navigate to="../evidence-integrity" replace />} />
            <Route path="/gaps-inconsistencies" element={<Navigate to="../evidence-integrity" replace />} />
          </Routes>
          )}
        </div>
      </div>
    </KeyFindingsProvider>
  );
};
